package nl.volleyanalyzer.web.controller

import nl.volleyplanner.web.util.jsObject
import kotlin.math.max


interface PouleStandingsChartData {
    val name: String
    fun generateDatasets(colorScheme: Map<String, String>) : Map<String, dynamic>
    fun generateYScaleConfig() : dynamic
}

abstract class AbstractPouleStandingsChartData(protected val pouleStandings: MultiRoundPouleStandings) :
    PouleStandingsChartData {


    abstract fun generateTeamChartData(pouleStandings: Collection<PouleStandings>, team: String) : Array<Number>

    override fun generateDatasets(colorScheme: Map<String, String>): Map<String, dynamic> {
        return pouleStandings.lastRoundStandings().records.keys.mapIndexed { index, team ->
            Pair(
                team,
                jsObject {
                    it.label = team
                    it.data = generateTeamChartData(pouleStandings.roundStandings.values, team)
                    it.segment = jsObject {
                        it.borderDash = { ctx: dynamic ->
                            val p1Round = ctx.p1DataIndex as Int
                            if (!pouleStandings.roundStandings.getValue(p1Round).records.getValue(team).playedThisRound) {
                                arrayOf(6, 6)
                            } else {
                                undefined
                            }
                        }
                    }
                    it.fill = false
                    it.borderColor = colorScheme.getValue(team)
                    it.tension = 0.05
                }
            )
        }.toMap()
    }
}

class PositionPouleStandingsChartData(pouleStandings: MultiRoundPouleStandings) : AbstractPouleStandingsChartData(pouleStandings) {
    override val name = "Positie"

    override fun generateTeamChartData(pouleStandings: Collection<PouleStandings>, team: String): Array<Number> {
        return pouleStandings.map { roundStandings -> roundStandings.records.keys.indexOf(team) + 1 }.toTypedArray()
    }

    override fun generateYScaleConfig(): dynamic {
        return jsObject {
            it.title = jsObject {
                it.display = true
                it.text = name
            }
            it.min = 1
            it.max = pouleStandings.teams.size
            it.reverse = true
            it.offset = true
            it.ticks = jsObject {
                it.count = pouleStandings.teams.size
                it.stepSize = 1
            }
        }
    }
}

class PointsPouleStandingsChartData(pouleStandings: MultiRoundPouleStandings) : AbstractPouleStandingsChartData(pouleStandings) {
    override val name = "Punten"

    override fun generateTeamChartData(pouleStandings: Collection<PouleStandings>, team: String): Array<Number> {
        return pouleStandings.map { roundStandings -> roundStandings.records.getValue(team).points }.toTypedArray()
    }

    override fun generateYScaleConfig(): dynamic {
        val gridSteps = pouleStandings.scoringMethod!!.possibleResults.values.maxOf { max(it.first, it.second) }

        val maxPoints = pouleStandings.roundStandings.values.last().records.values.maxOf { it.points } + gridSteps
        return jsObject {
            it.title = jsObject {
                it.display = true
                it.text = name
            }
            it.min = 0
            it.max = maxPoints
            it.reverse = false
            it.offset = false
            it.ticks = jsObject {
                it.count = maxPoints / gridSteps
                it.stepSize = gridSteps
            }
        }
    }
}

class SetsWonPouleStandingsChartData(pouleStandings: MultiRoundPouleStandings) : AbstractPouleStandingsChartData(pouleStandings) {
    override val name = "Gewonnen Sets"

    override fun generateTeamChartData(pouleStandings: Collection<PouleStandings>, team: String): Array<Number> {
        return pouleStandings.map { roundStandings -> roundStandings.records.getValue(team).setsWon }.toTypedArray()
    }

    override fun generateYScaleConfig(): dynamic {
        val gridSteps = pouleStandings.scoringMethod!!.possibleResults.keys.maxOf { max(it.first, it.second) }

        val maxPoints = pouleStandings.roundStandings.values.last().records.values.maxOf { it.setsWon } + gridSteps
        return jsObject {
            it.title = jsObject {
                it.display = true
                it.text = name
            }
            it.min = 0
            it.max = maxPoints
            it.reverse = false
            it.offset = false
            it.ticks = jsObject {
                it.count = maxPoints / gridSteps
                it.stepSize = gridSteps
            }
        }
    }
}

class SetQuotientPouleStandingsChartData(pouleStandings: MultiRoundPouleStandings) : AbstractPouleStandingsChartData(pouleStandings) {
    override val name = "Set Quotient"

    override fun generateTeamChartData(pouleStandings: Collection<PouleStandings>, team: String): Array<Number> {
        return pouleStandings.map { roundStandings -> roundStandings.records.getValue(team).setQuotient }.toTypedArray()
    }

    override fun generateYScaleConfig(): dynamic {
        val maxPoints = pouleStandings.roundStandings.values.last().records.values.maxOf { it.setQuotient }

        return jsObject {
            it.title = jsObject {
                it.display = true
                it.text = name
            }
            it.min = 0
            it.max = maxPoints
            it.reverse = false
            it.offset = true
//            it.ticks = jsObject {
//                it.count = maxSets.toDouble() / 0.5
//                it.stepSize = 0.5
//            }
        }
    }
}

class AverageSetQuotientPouleStandingsChartData(pouleStandings: MultiRoundPouleStandings) : AbstractPouleStandingsChartData(pouleStandings) {
    override val name = "Gemiddelde Set Quotient"

    // TODO: Fix this one. And what average do we want?
    //  The standings are cumulative. So we can't take the average from each match easily
    override fun generateTeamChartData(pouleStandings: Collection<PouleStandings>, team: String): Array<Number> {
        var setQuotient = 0.0
        // For average drop the first (non match round) so it doesn't mess up the average
        return pouleStandings.drop(1).mapIndexed { index, roundStandings ->
            setQuotient += roundStandings.records.getValue(team).setQuotient
            setQuotient / (index + 1).toDouble()
        }.toTypedArray()
    }

    override fun generateYScaleConfig(): dynamic {
        val maxSets = pouleStandings.scoringMethod!!.possibleResults.keys.maxOf { max(it.first, it.second) }
        return jsObject {
            it.title = jsObject {
                it.display = true
                it.text = name
            }
            it.min = 0
            //it.max = maxSets
            it.reverse = false
            it.offset = true
//            it.ticks = jsObject {
//                it.count = maxSets.toDouble() / 0.5
//                it.stepSize = 0.5
//            }
        }
    }
}

class PointsWonPouleStandingsChartData(pouleStandings: MultiRoundPouleStandings) : AbstractPouleStandingsChartData(pouleStandings) {
    override val name = "Punten Voor"

    override fun generateTeamChartData(pouleStandings: Collection<PouleStandings>, team: String): Array<Number> {
        return pouleStandings.map { roundStandings -> roundStandings.records.getValue(team).pointsWon }.toTypedArray()
    }

    override fun generateYScaleConfig(): dynamic {
        val gridSteps = 25
        val maxPoints = pouleStandings.roundStandings.values.last().records.values.maxOf { it.pointsWon } + gridSteps
        return jsObject {
            it.title = jsObject {
                it.display = true
                it.text = name
            }
            it.min = 0
            it.max = maxPoints
            it.reverse = false
            it.offset = false
            it.ticks = jsObject {
                it.count = maxPoints / gridSteps
                it.stepSize = gridSteps
            }
        }
    }
}
//
//
//enum class PouleStandingsChartType(chartDataFunction: (pouleStandings: Collection<PouleStandings>, team: String) -> Array<Number>) {
//    PointQuotient({ pouleStandings, team ->
//        pouleStandings.map { roundStandings -> roundStandings.records.getValue(team).pointsQuotient }.toTypedArray()
//    }),
//    AveragePointQuotient({ pouleStandings, team ->
//        var pointsQuotient = 0.0
//        pouleStandings.mapIndexed { index, roundStandings ->
//            pointsQuotient += roundStandings.records.getValue(team).pointsQuotient
//            pointsQuotient / (index + 1).toDouble()
//        }.toTypedArray()
//    }),
//}